import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { EEnvironment, ENVIRONMENT } from '@redngapps/shared/env/types';
import { Logger } from '@redngapps/shared/logging/types';
import { logMessage } from '@redngapps/shared/logging/util';
import { Nullable } from '@redngapps/shared/types';
import { isNil } from 'lodash-es';
import { firstValueFrom, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { AbstractRuntimeConfigService } from './abstract-runtime-config.service';
import { IRuntimeConfigBase } from './runtime-config-base.interface';

const RUNTIME_CONFIG_PATH = '/assets/runtime-config.json';

/**
 * Loads `/assets/runtime-config.json` (rendered by the nginx entrypoint from container env vars)
 * during `APP_INITIALIZER` and wires the resolved server host/port/protocol into the RED API. This
 * is the single source of the instance's service URL — never hardcoded in the bundle. Apps subclass
 * this with their concrete config type and provide their concrete config service.
 */
@Injectable()
export abstract class AbstractRuntimeConfigInitializerService<T extends IRuntimeConfigBase> {
  protected abstract readonly configService: AbstractRuntimeConfigService<T>;
  private readonly http = inject(HttpClient);
  private readonly logger = inject(Logger);
  private readonly environment = inject(ENVIRONMENT);

  async initialize(): Promise<Nullable<T>> {
    return firstValueFrom(
      this.http.get<Nullable<T>>(RUNTIME_CONFIG_PATH).pipe(
        tap(config => {
          if (!isNil(config)) {
            this.configService.setConfig(config);
            red.setHostCredentials(config.serverHost, config.serverPort, config.serverProtocol);
          }
        }),
        catchError((error: unknown) => {
          // Locally (`ng serve`) there is no runtime-config.json — the dev proxy is used instead, so
          // the 404 is expected and stays silent. Every deployed environment has nginx render the
          // file, so a failure there is a real misconfiguration that would otherwise leave the app
          // without server credentials and fail opaquely.
          if (this.environment.name !== EEnvironment.development) {
            this.logger.warn(
              logMessage`Runtime config could not be loaded from /assets/runtime-config.json; the app runs without server credentials.`,
              error,
            );
          }

          return of(null);
        }),
      ),
    );
  }
}
